import { useEffect, useRef } from "react";
import { useInView } from "framer-motion";

// Pixel-sort style reveal: image resolves from large pixel blocks to sharp (tamasolajos.com signature)
export default function PixelImage({
    src,
    alt = "",
    fit = "cover",
    className = "",
    duration = 1500,
    maxPixel = 56,
    delay = 0,
}) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const inView = useInView(wrapRef, { once: true, margin: "-40px" });

    useEffect(() => {
        if (!inView) return;
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const off = document.createElement("canvas");
        const octx = off.getContext("2d");
        const img = new Image();
        img.src = src;
        let raf;

        const render = (pixel) => {
            const w = wrap.clientWidth;
            const h = wrap.clientHeight;
            if (!w || !h || !img.naturalWidth) return;
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w;
                canvas.height = h;
            }
            const iw = img.naturalWidth;
            const ih = img.naturalHeight;
            const scale = fit === "cover" ? Math.max(w / iw, h / ih) : Math.min(w / iw, h / ih);
            const dw = iw * scale;
            const dh = ih * scale;
            const dx = (w - dw) / 2;
            const dy = (h - dh) / 2;
            ctx.clearRect(0, 0, w, h);
            if (pixel <= 1.5) {
                ctx.imageSmoothingEnabled = true;
                ctx.drawImage(img, dx, dy, dw, dh);
                return;
            }
            const sw = Math.max(1, Math.round(w / pixel));
            const sh = Math.max(1, Math.round(h / pixel));
            off.width = sw;
            off.height = sh;
            octx.imageSmoothingEnabled = true;
            octx.drawImage(img, (dx * sw) / w, (dy * sh) / h, (dw * sw) / w, (dh * sh) / h);
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(off, 0, 0, sw, sh, 0, 0, w, h);
        };

        img.onload = () => {
            const t0 = performance.now() + delay;
            const tick = (now) => {
                const p = Math.min(1, Math.max(0, (now - t0) / duration));
                const eased = 1 - Math.pow(1 - p, 3);
                render(maxPixel * (1 - eased) + eased);
                if (p < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
        };
        if (img.complete && img.naturalWidth) img.onload();
        return () => cancelAnimationFrame(raf);
    }, [inView, src, fit, duration, maxPixel, delay]);

    return (
        <div ref={wrapRef} role="img" aria-label={alt} className={className}>
            <canvas ref={canvasRef} className="block h-full w-full" />
        </div>
    );
}
